import { PersistentStorage } from 'apollo3-cache-persist/lib/types'

// Check if IndexedDb is supported
export const isIndexedDBSupported = (): boolean => {
  try {
    // Check if IndexedDB is available in the global window object
    if (!('indexedDB' in window)) {
      return false
    }

    // Additional check to ensure it's not just a placeholder
    if (typeof window.indexedDB.open !== 'function') {
      return false
    }

    // You can add more specific checks if needed
    return true
  } catch (error) {
    // If any error occurs during the check, assume it's not supported
    return false
  }
}

/**
 * Custom storage wrapper that stores Apollo cache data in IndexedDB.
 *
 * This implementation uses IndexedDB directly without any additional libraries.
 */
export class ApolloPersistedStorage implements PersistentStorage<string> {
  private readonly dbName: string
  private readonly storeName: string
  private readonly dbVersion: number
  private dbPromise: Promise<IDBDatabase> | null = null

  /**
   * Creates a new instance of ApolloPersistedStorage
   *
   * @param dbName - The name of the IndexedDB database
   * @param storeName - The name of the object store
   * @param dbVersion - The version of the database
   */
  constructor(dbName = 'rooute', storeName = 'apollo', dbVersion = 1) {
    this.dbName = dbName
    this.storeName = storeName
    this.dbVersion = dbVersion
  }

  /**
   * Gets an item from the cache
   *
   * @param key - The key to get (used as a base key for all entries)
   * @returns A promise that resolves to the combined cache data as a JSON string
   */
  async getItem(key: string): Promise<string | null> {
    try {
      // Get all keys from storage
      const allKeys = await this.getAllKeys()

      if (allKeys.length === 0) {
        return null
      }

      // Retrieve all cache entries
      const entries: Record<string, unknown> = {}
      await Promise.all(
        allKeys.map(async (cacheKey) => {
          const value = await this.getValue(cacheKey)
          if (value !== null && value !== undefined) {
            entries[cacheKey] = value
          }
        }),
      )

      // Return the combined cache data
      return JSON.stringify(entries)
    } catch (error) {
      console.error(`Error retrieving cache data:`, error)
      return null
    }
  }

  /**
   * Sets an item in the cache
   *
   * @param key - The key to set (used as a base key for all entries)
   * @param value - The value to set as a JSON string
   * @returns A promise that resolves when the item is set
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      // Parse the cache data
      const cacheData = JSON.parse(value)

      // Store each top-level entry separately
      const promises = Object.entries(cacheData).map(([entryKey, entryValue]) => {
        return this.setValue(entryKey, entryValue)
      })

      await Promise.all(promises)
    } catch (error) {
      console.error(`Error storing cache data:`, error)
    }
  }

  /**
   * Removes an item from the cache
   *
   * @param key - The key to remove (used as a base key for all entries)
   * @returns A promise that resolves when the item is removed
   */
  async removeItem(key: string): Promise<void> {
    try {
      // Get all keys from storage
      const allKeys = await this.getAllKeys()

      // Remove all cache entries
      await Promise.all(allKeys.map((cacheKey) => this.deleteValue(cacheKey)))
    } catch (error) {
      console.error(`Error removing cache data:`, error)
    }
  }

  /**
   * Clears all data from the IndexedDB storage
   *
   * @returns A promise that resolves when all data is cleared
   */
  async clear(): Promise<void> {
    try {
      const db = await this.getDB()

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite')
        const objectStore = transaction.objectStore(this.storeName)
        const request = objectStore.clear()

        request.onerror = (event) => {
          console.error('Error clearing IndexedDB:', event)
          reject(new Error('Could not clear IndexedDB'))
        }

        request.onsuccess = () => {
          console.log('IndexedDB cleared successfully')
          resolve()
        }
      })
    } catch (error) {
      console.error('Error clearing IndexedDB:', error)
    }
  }

  /**
   * Opens a connection to the IndexedDB database
   *
   * @returns A promise that resolves to an IDBDatabase instance
   */
  private getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)

      request.onerror = (event) => {
        console.error('Error opening IndexedDB:', event)
        reject(new Error('Could not open IndexedDB'))
      }

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        resolve(db)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create the object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName)
        }
      }
    })

    return this.dbPromise
  }

  /**
   * Gets all keys from the object store
   *
   * @returns A promise that resolves to an array of keys
   */
  private async getAllKeys(): Promise<string[]> {
    const db = await this.getDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly')
      const objectStore = transaction.objectStore(this.storeName)
      const request = objectStore.getAllKeys()

      request.onerror = (event) => {
        console.error('Error getting keys from IndexedDB:', event)
        reject(new Error('Could not get keys from IndexedDB'))
      }

      request.onsuccess = (event) => {
        const keys = (event.target as IDBRequest).result as string[]
        const filteredKeys = keys.filter((k) => typeof k === 'string')
        resolve(filteredKeys)
      }
    })
  }

  /**
   * Gets a value from the object store
   *
   * @param key - The key to get
   * @returns A promise that resolves to the value
   */
  private async getValue(key: string): Promise<unknown> {
    const db = await this.getDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readonly')
      const objectStore = transaction.objectStore(this.storeName)
      const request = objectStore.get(key)

      request.onerror = (event) => {
        console.error(`Error getting value for key ${key}:`, event)
        reject(new Error(`Could not get value for key ${key}`))
      }

      request.onsuccess = (event) => {
        const value = (event.target as IDBRequest).result
        resolve(value)
      }
    })
  }

  /**
   * Sets a value in the object store
   *
   * @param key - The key to set
   * @param value - The value to set
   * @returns A promise that resolves when the value is set
   */
  private async setValue(key: string, value: unknown): Promise<void> {
    const db = await this.getDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite')
      const objectStore = transaction.objectStore(this.storeName)
      const request = objectStore.put(value, key)

      request.onerror = (event) => {
        console.error(`Error setting value for key ${key}:`, event)
        reject(new Error(`Could not set value for key ${key}`))
      }

      request.onsuccess = () => {
        resolve()
      }
    })
  }

  /**
   * Deletes a value from the object store
   *
   * @param key - The key to delete
   * @returns A promise that resolves when the value is deleted
   */
  private async deleteValue(key: string): Promise<void> {
    const db = await this.getDB()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], 'readwrite')
      const objectStore = transaction.objectStore(this.storeName)
      const request = objectStore.delete(key)

      request.onerror = (event) => {
        console.error(`Error deleting value for key ${key}:`, event)
        reject(new Error(`Could not delete value for key ${key}`))
      }

      request.onsuccess = () => {
        resolve()
      }
    })
  }
}
